import React from 'react';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Tabs,
  Tab,
  Paper,
  useMediaQuery,
  useTheme,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { PatchType } from '../../../types';

type PatchTypeSelectorProps = {
  value: PatchType;
  onChange: (patchType: PatchType) => void;
  variant?: 'tabs' | 'radio' | 'select';
}

const PatchTypeSelector: React.FC<PatchTypeSelectorProps> = ({
  value,
  onChange,
  variant = 'select',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Force select variant on mobile
  const effectiveVariant = isMobile ? 'select' : variant;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: PatchType) => {
    onChange(newValue);
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value as PatchType);
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    onChange(event.target.value as PatchType);
  };

  const renderSelector = () => {
    switch (effectiveVariant) {
      case 'tabs':
        return (
          <Paper sx={{ mb: 2 }}>
            <Tabs
              value={value}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              data-testid='patchTypeSelect'
            >
              <Tab label="Product Price" value={PatchType.PRODUCT_PRICE} />
              <Tab label="Product Exclusion" value={PatchType.PRODUCT_EXCLUSION} />
              <Tab label="Bundle Price" value={PatchType.BUNDLE_PRICE} />
              <Tab label="Bundle Exclusion" value={PatchType.BUNDLE_EXCLUSION} />
            </Tabs>
          </Paper>
        );
      case 'radio':
        return (
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend">Patch Type</FormLabel>
            <RadioGroup row value={value} onChange={handleRadioChange} data-testid='patchTypeSelect'>
              <FormControlLabel
                value={PatchType.PRODUCT_PRICE}
                control={<Radio />}
                label="Product Price"
              />
              <FormControlLabel
                value={PatchType.PRODUCT_EXCLUSION}
                control={<Radio />}
                label="Product Exclusion"
              />
              <FormControlLabel
                value={PatchType.BUNDLE_PRICE}
                control={<Radio />}
                label="Bundle Price"
              />
              <FormControlLabel
                value={PatchType.BUNDLE_EXCLUSION}
                control={<Radio />}
                label="Bundle Exclusion"
              />
            </RadioGroup>
          </FormControl>
        );
      case 'select':
        return (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <FormLabel component="legend">Patch Type</FormLabel>
            <Select value={value} onChange={handleSelectChange} data-testid='patchTypeSelect'>
              <MenuItem value={PatchType.PRODUCT_PRICE}>Product Price</MenuItem>
              <MenuItem value={PatchType.PRODUCT_EXCLUSION}>Product Exclusion</MenuItem>
              <MenuItem value={PatchType.BUNDLE_PRICE}>Bundle Price</MenuItem>
              <MenuItem value={PatchType.BUNDLE_EXCLUSION}>Bundle Exclusion</MenuItem>
            </Select>
          </FormControl>
        );
      default:
        return null;
    }
  };

  return <Box>{renderSelector()}</Box>;
};

export default PatchTypeSelector;
